import { useAuth } from '../context/AuthContext';
import { Permission, getRolePermissions, canAccessPage, userHasPermission } from '../utils/permissions';
import { UserRole } from '../types';

/**
 * Hook for checking user permissions based on their role
 */
export const usePermissions = () => {
  const { user } = useAuth();
  
  const userRole = user?.role || UserRole.END_USER;

  /**
   * Check if current user has a specific permission
   */
  const can = (permission: Permission): boolean => {
    return userHasPermission(user, permission);
  };

  /**
   * Check if current user has any of the specified permissions
   */
  const canAny = (permissions: Permission[]): boolean => {
    return permissions.some((permission) => userHasPermission(user, permission));
  };

  /**
   * Check if current user has all of the specified permissions
   */
  const canAll = (permissions: Permission[]): boolean => {
    return permissions.every((permission) => userHasPermission(user, permission));
  };

  /**
   * Get all permissions for current user
   */
  const permissions = getRolePermissions(userRole);

  /**
   * Check if current user can access a specific page
   */
  const canAccessRoute = (page: string): boolean => {
    return canAccessPage(userRole, page);
  };

  /**
   * Check if current user is a specific role
   */
  const isRole = (role: UserRole): boolean => {
    return userRole === role;
  };

  /**
   * Check if current user is any of the specified roles
   */
  const isAnyRole = (roles: UserRole[]): boolean => {
    return roles.includes(userRole);
  };

  /**
   * Check if user is admin or higher (CLIENT_ADMIN ili SUPER_ADMIN)
   */
  const isAdmin = (): boolean => {
    return isAnyRole([UserRole.CLIENT_ADMIN, UserRole.SUPER_ADMIN]);
  };

  /**
   * Check if user is super admin
   */
  const isSuperAdmin = (): boolean => {
    return isRole(UserRole.SUPER_ADMIN);
  };

  return {
    can,
    canAny,
    canAll,
    canAccessRoute,
    isRole,
    isAnyRole,
    isAdmin,
    isSuperAdmin,
    permissions,
    role: userRole,
    user
  };
};

export default usePermissions;

